/* eslint-disable max-lines */
/* eslint-disable react/no-array-index-key */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { mediaQuery, Spacings } from '@styles/index';
import CarouselFeed from '@cont/NewPreSubscribed/CarouselFeed';
import GetSubscriptionBanner from '@cont/StoreListing/GetSubscriptionBanner';
import StoreCard from '@cont/StoreListing/StoreCard';
import OfflineCentreBanner from '@cont/StoreListing/OfflineCentreBanner';
import { useIsMobile } from '@stores/AppCommonData';
import useClickAwayListener from '@hooks/useClickAwayListener';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { segment } from '@actions/analytics';
import { getStoreListingEndpoint } from '@api/fetchers/store';
import fetch from '@api/fetchers/index';
import segmentStorage from '@events/segmentStorage';
import isEmpty from 'lodash/isEmpty';
import styled from '@emotion/styled';
import Dynamic from '@base/Dynamic/Dynamic';
import {
  ALL,
  STORE_TAB_FILTER_APPLIED,
  STORE_LANDING_PAGE_VIEWED
} from './constants';
import Filters from './Filters';
import {
  clearFilter,
  getAllOptionsByFiltersName,
  getFilterObjectFromRoute,
  getFiltersApplied,
  getFiltersDefaultState,
  getFiltersPayload,
  getNewFilterState,
  onFilterApply,
  routerReplace
} from './storeListingHelper';

const SortByFilterComponent = Dynamic(() => import('./SortByFilterComponent'));
const CircularLoader = Dynamic(
  () => import('@base/CircularLoader/CircularLoader')
);
const EmptyState = Dynamic(() => import('@comp/EmptyState/EmptyState'));
const InfiniteFeedRow = Dynamic(
  () => import('@cont/SWRInfiniteFeedRow/InfiniteFeedRow')
);

const Loader = () => (
  <ProgressWrapper>
    <CircularLoader />
  </ProgressWrapper>
);

const ProgressWrapper = styled.div`
  grid-column: span 18;
  margin: auto;
  height: ${Spacings.SPACING_30B};
  display: flex;
  justify-content: center;
  align-items: center;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    grid-column: span 10;
  }
`;

const BodyWrapper = styled.div<{ $noBanner: boolean }>`
  display: flex;
  flex-direction: row;
  width: 1136px;
  margin: 0px auto;
  margin-top: ${({ $noBanner }) =>
    $noBanner ? `${Spacings.SPACING_0B}` : `${Spacings.SPACING_12B}`};
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    width: 100%;
    margin-top: ${({ $noBanner }) =>
      $noBanner ? `${Spacings.SPACING_0B}` : `${Spacings.SPACING_2B}`};
  }
`;

const Wrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_25B};
  display: flex;
  flex-direction: column;
  ${mediaQuery.aboveMobile} {
    border-left: 1px solid var(--color-i-divider);
    padding-left: ${Spacings.SPACING_16B};
  }
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_4B};
    margin-left: ${Spacings.SPACING_4B};
    margin-right: ${Spacings.SPACING_4B};
  }
`;

const RightWrapper = styled.div`
  width: 855px;
  position: relative;
  .no-filter-store {
    margin: auto;
    margin-top: 280px;
    ${mediaQuery.uptoMobile} {
      margin-top: 65px;
      img {
        max-width: ${Spacings.SPACING_20B};
        max-height: ${Spacings.SPACING_20B};
      }
    }
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const RowWrapper = styled.div`
  width: 848px;
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_6B};
  column-gap: ${Spacings.SPACING_4B};
  align-items: flex-start;
  flex-wrap: wrap;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    gap: ${Spacings.SPACING_4B};
  }
`;

const SortBy = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_10B};
  margin-top: ${Spacings.SPACING_6B};
`;

let EventFilterName = '';

let childCardsState = {};
let resetState = false;
const limit = 12;
let nextCount = 0;

const StoreListing = ({ filtersData }) => {
  const router = useRouter();
  const { query } = router;
  const goalUID = query.goalUID as string;
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  let { sorters, filters: filterSource } = filtersData || {};
  const goalSegmentData = useGoalEventProps({ goalUID });
  const isMobile = useIsMobile();
  const rowCount = isMobile ? 1 : 3;
  const positionCount = isMobile ? 1 : 2;

  sorters = sorters ?? [];
  filterSource = filterSource ?? [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filters = useMemo(() => [...sorters, ...filterSource], []);

  const bottomSheetWrapRef = useRef();
  const [openBottomSheet, setOpenBottomSheet] = useState(false);
  const [filterObj, setFilterObj] = useState({});
  const [superSetObj, setSuperSetFilterObj] = useState({});
  const [skuData, setSkuData] = useState([]);
  const [listingData, setListingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [metaConfig, setMetaConfig] = useState(null);
  const [paginatedCards, setPaginatedCards] = useState([]);
  const [allCardsFetched, setAllCardsFetched] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(true);
  const [showResetBtn, setShowResetBtn] = useState(false);
  const [routeStateChanged, setRouteStateChanged] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(
    getFiltersApplied({ superSetObj, filters })
  );
  const [isMountEvent, setIsMountEvent] = useState(false);

  const { banners = [] } = listingData || {};

  const filtersPayload = useMemo(() => {
    if (isEmpty(superSetObj) || resetState) return null;
    const payload = getFiltersPayload({ superSetObj, sorters });
    setFiltersChanged(true);
    return payload;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [superSetObj, routeStateChanged]);

  const setChildCards = useCallback(() => {
    const {
      cards: { metaData: { metaDataConfig: { childCards = null } = {} } = {} }
    } = listingData;
    const minimumPosition = isMobile ? 2 : 3;
    if (!childCards) childCardsState = {};
    childCards?.forEach((card: any) => {
      const position =
        card.position < minimumPosition ? minimumPosition : card.position;
      const adjustedPosition = (position - positionCount) * rowCount;
      childCardsState[adjustedPosition] = {
        type: card.cardType,
        repeat: card.repeat,
        index: adjustedPosition
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingData]);

  useFirstRenderSkip(() => {
    setFiltersChanged(true);
  }, [isLoggedIn]);

  useEffect(() => {
    if (filtersChanged) {
      setIsLoading(true);
      setAllCardsFetched(false);
      setFiltersChanged(false);
      setFiltersApplied(getFiltersApplied({ superSetObj, filters }));
      nextCount = 0;
      fetch(getStoreListingEndpoint(goalUID), {
        requestType: 'POST',
        body: filtersPayload
      })
        .then((data) => {
          if (data) {
            const formattedData = { ...data };
            if (
              formattedData?.cards?.metaData &&
              !formattedData.cards.metaData.metaDataConfig
            ) {
              formattedData.cards.metaData.metaDataConfig = {
                rootCard: 'skus'
              };
            }
            setListingData(formattedData);
          }
        })
        .catch((err) => {})
        .finally(() => {
          setIsLoading(false);
          resetState = false;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersChanged, routeStateChanged, isLoggedIn]);

  useEffect(() => {
    setRouteStateChanged(!routeStateChanged);
    setFiltersChanged(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const loadMoreSkus = () => {
    nextCount += limit;

    setIsLoading(true);
    fetch(getStoreListingEndpoint(goalUID, nextCount), {
      requestType: 'POST',
      body: filtersPayload
    })
      .then((data) => {
        if (data) {
          addCards(data.cards);
        }
      })
      .catch((err) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!isEmpty(listingData?.cards?.metaData?.metaDataConfig)) {
      setChildCards();
      setMetaConfig(listingData?.cards?.metaData?.metaDataConfig);
    } else {
      childCardsState = {};
    }
  }, [listingData, setChildCards]);

  useEffect(() => {
    if (!metaConfig || !listingData) return;

    const { rootCard } = metaConfig;

    setPaginatedCards(listingData.cards[rootCard] || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingData, metaConfig]);

  const addCards = useCallback(
    (cards) => {
      if (!metaConfig) return;

      const { rootCard } = metaConfig;

      if (cards[rootCard]?.length)
        setPaginatedCards((curr) => [...curr, ...cards[rootCard]]);
      else setAllCardsFetched(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [metaConfig]
  );

  useEffect(() => {
    // sku length < child card position, insert child cards at the end
    const components = [...skuData];
    const cardsTypes = { subscription: 1, centre: 1 };
    if (Object.keys(childCardsState).length > 0) {
      components.forEach((c) => {
        if (cardsTypes[c.type]) {
          delete cardsTypes[c.type];
        }
      });
      if (Object.keys(cardsTypes).length > 0) {
        Object.keys(childCardsState)?.forEach((cardIndex) => {
          if (Number(cardIndex) > skuData.length) {
            const { type } = childCardsState[cardIndex];
            const isChildCard = listingData.cards[type];
            if (isChildCard && Object.keys(isChildCard)?.length > 0) {
              components.push({
                type,
                data: { ...isChildCard, lastChild: true }
              });
            }
          }
        });
        if (components.length > skuData.length) {
          childCardsState = {};
          setSkuData(components);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuData.length]);

  useEffect(() => {
    if (!metaConfig || !listingData) return;

    segmentStorage.setItem('lps', 'store');

    const components = [];
    paginatedCards.forEach((card, i) => {
      const isChildCard = childCardsState[i];
      const childCardData = listingData.cards[isChildCard?.type];
      if (
        isChildCard &&
        childCardData &&
        Object.keys(childCardData)?.length > 0
      ) {
        components.push({ type: isChildCard.type, data: childCardData });
        if (isChildCard.repeat) {
          childCardsState[i + isChildCard.index] = isChildCard;
        }
      }
      components.push({ type: 'sku', data: card });
    });

    setSkuData(components);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginatedCards, rowCount, positionCount]);

  const resetFilters = (fetchNewData) => {
    setShowResetBtn(false);
    resetState = true;
    const defaultState = getFiltersDefaultState({ filters });

    setFilterObj(defaultState);
    setSuperSetFilterObj(defaultState);
    setFiltersApplied(getFiltersApplied({ superSetObj, filters }));
    setOpenBottomSheet(false);
    if (fetchNewData) {
      setFiltersChanged(true);
      const queryParams = { ...router.query };
      const { goalSlug, goalUID } = queryParams;
      routerReplace({ router, queryParams: { goalSlug, goalUID } });
    }
  };

  useEffect(() => {
    resetFilters(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (!router.isReady) return;
    const { lps, lpss } = query;

    if (!isMountEvent) {
      setIsMountEvent(true);
      segment.track(STORE_LANDING_PAGE_VIEWED, {
        ...goalSegmentData,
        last_primary_source: lps,
        last_primary_source_section: lpss
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useFirstRenderSkip(() => {
    const filterTypes = [];
    const filterValues = [];

    Object.entries(filterObj).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        filterTypes.push(key);
        filterValues.push(value.join(','));
      }
    });

    const filterSortByRelevanceExist = filterValues.indexOf('1');

    if (EventFilterName !== '' && !isMobile) {
      segment.track(STORE_TAB_FILTER_APPLIED, {
        ...goalSegmentData,
        filter_type: [EventFilterName],
        filter_value: [superSetObj[EventFilterName].join(',')],
        filter_type_count:
          filterSortByRelevanceExist !== -1
            ? filterTypes.length - 1
            : filterTypes.length
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [superSetObj]);

  useClickAwayListener(bottomSheetWrapRef, () => {
    setFilterObj({ ...superSetObj });
    setOpenBottomSheet(false);
  });

  const getAllOptionsByFilterName = (key) => {
    return getAllOptionsByFiltersName({ key, filters });
  };

  const onApply = () => {
    setSuperSetFilterObj({ ...filterObj });
    setFiltersChanged(true);
    setOpenBottomSheet(false);

    const filterTypes = [];
    const filterValues = [];

    Object.entries(filterObj).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        filterTypes.push(key);
        filterValues.push(value.join(','));
      }
    });
    const filterSortByRelevanceExist = filterValues.indexOf('1');

    segment.track(STORE_TAB_FILTER_APPLIED, {
      ...goalSegmentData,
      filter_type: filterTypes,
      filter_value: filterValues,
      filter_type_count:
        filterSortByRelevanceExist !== -1
          ? filterTypes.length - 1
          : filterTypes.length
    });
  };

  const onClear = (filter) => {
    const clearState = clearFilter({ filterObj, filter });
    setFilterObj(clearState);
    setSuperSetFilterObj(clearState);
    setFiltersChanged(true);
  };

  const onChange = ({ value, filterName, filterType }) => {
    EventFilterName = filterName;
    const newState = getNewFilterState({
      filterType,
      value,
      filterObj,
      filterName,
      filters
    });

    setFilterObj({
      ...filterObj,
      [filterName]: newState
    });
    if (!isMobile) {
      setSuperSetFilterObj({
        ...filterObj,
        [filterName]: newState
      });
      setFiltersChanged(true);
    }
    setShowResetBtn(true);
    const newFilterObj = { ...filterObj, [filterName]: newState };

    const queryParams = { ...router.query };

    Object.entries(newFilterObj).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        queryParams[key] = value.join(',') || [];
      }
    });

    routerReplace({ router, queryParams });
  };

  useEffect(() => {
    if (filtersApplied === 0) setShowResetBtn(false);
  }, [filtersApplied]);

  useEffect(() => {
    const newFilterObj: any = { ...router.query };
    const defaultState = getFiltersDefaultState({ filters });

    if (Object.keys(newFilterObj).length > 2 && !routeStateChanged) {
      const filterObjFromRoute = getFilterObjectFromRoute({
        defaultState,
        newFilterObj,
        filters
      });

      if (
        Object.keys(filterObjFromRoute).length !== 0 &&
        Object.values(filterObjFromRoute).filter(
          (filterVal: Array<string>) => filterVal.length !== 0
        ).length !== 0
      ) {
        resetState = false;
        setFilterObj({ ...filterObjFromRoute });
        setSuperSetFilterObj({ ...filterObjFromRoute });
        setFiltersChanged(true);
        setShowResetBtn(true);
        setRouteStateChanged(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query, routeStateChanged]);

  const renderComponent = ({ type, data }) => {
    switch (type) {
      case 'centre':
        return <OfflineCentreBanner data={data} />;
      case 'subscription':
        return <GetSubscriptionBanner data={data} />;
      case 'sku':
        return <StoreCard data={data} />;
      default:
        return null;
    }
  };

  if (!listingData) return null;

  return (
    <>
      {banners?.data?.length ? (
        <CarouselFeed block={banners} source="store" />
      ) : null}
      <BodyWrapper $noBanner={!!banners?.data?.length}>
        <Filters
          filters={filters}
          onChange={onChange}
          onClear={onClear}
          onApply={onApply}
          filterObj={filterObj}
          openBottomSheet={openBottomSheet}
          setOpenBottomSheet={setOpenBottomSheet}
          bottomSheetWrapRef={bottomSheetWrapRef}
          resetFilters={resetFilters}
          showResetBtn={showResetBtn}
          filtersApplied={filtersApplied}
          getAllOptionsByFilterName={getAllOptionsByFilterName}
        />
        <Wrapper>
          {!isMobile && (
            <SortBy>
              <SortByFilterComponent
                // @ts-ignore
                filters={filters}
                setOpenBottomSheet={setOpenBottomSheet}
                superSetObj={superSetObj}
                onChange={onChange}
              />
            </SortBy>
          )}
          <RightWrapper>
            {paginatedCards.length > 0 ? (
              <RowWrapper>
                <InfiniteFeedRow
                  // @ts-ignore
                  data={skuData}
                  isLoading={isLoading || allCardsFetched}
                  renderItem={renderComponent}
                  onLoadMore={loadMoreSkus}
                  renderLoader={allCardsFetched ? () => null : Loader}
                />
              </RowWrapper>
            ) : (
              <EmptyState
                // @ts-ignore
                title="No result found"
                description="Try changing filters to see more results"
                className="no-filter-store"
              />
            )}
          </RightWrapper>
        </Wrapper>
      </BodyWrapper>
    </>
  );
};

export default StoreListing;
